import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { PhxTableColumnConfig, PhxTableData, PhxStickyColumnInfo, PhxCellStyleInfo } from '../phx-table.types';
import { PhxTableService } from '../phx-table.service';
import { PhxCellDefDirective } from '../cell/phx-cell-def.directive';
import { PhxGroupHeaderDirective } from './phx-group-header.directive';
import { trackByColumnKey } from '../phx-table.utils';
import { Bullet16, ChevronDown } from '../../icon/icon-map';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'tr[phx-table-row]',
  exportAs: 'phxTableRow',
  templateUrl: './phx-table-row.component.html',
  styleUrls: ['./phx-table-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'phx-table-row',
    '[class.phx-table-row-selected]': 'selected',
    '[class.phx-table-row-disabled]': 'phxDisabled',
    '[class.phx-table-row-clickable]': 'isClickable',
    '[class.phx-table-row-group-row]': 'phxGroupRow',
    '(click)': 'onClick()',
  },
})
export class PhxTableRowComponent<T extends PhxTableData = PhxTableData>
  implements OnChanges, AfterContentInit, OnDestroy
{
  /** Row payload used for selection and disabled-state tracking; should include a unique `id`. */
  @Input() phxRowData: T | undefined;
  /** When true, the row cannot be selected or clicked. */
  @Input() phxDisabled: boolean = false;
  /** Row-level clickable override; falls back to the table's `phxClickable` when unset. */
  @Input() phxClickable: boolean | undefined;
  /** When false, suppresses the auto-drawn checkbox cell (for non-leader rows in a rowspan group). */
  @Input() phxShowSelectionCell: boolean = true;
  /** Rowspan for the auto-drawn checkbox cell on the group leader row. */
  @Input() phxSelectionRowspan: number | undefined;

  // ── Collapsible group header ──────────────────────────────
  /** Marks this row as a collapsible group header instead of a normal data row. */
  @Input() phxGroupRow: boolean = false;
  /** Whether the group is expanded. Two-way bindable; read `open` via `#g="phxTableRow"`. */
  @Input() phxGroupOpen: boolean = false;
  /** Primary title text for the structured group header (rendered bold). */
  @Input() phxGroupTitle: string | undefined;
  /** Secondary muted text shown after the title with a `·` separator. */
  @Input() phxGroupSubtitle: string | undefined;
  /** Item count rendered inside a `phx-badge`. Badge is hidden when unset. */
  @Input() phxGroupItemsCount: number | undefined;
  /** When false, hides the group-header checkbox even when the table is selectable. */
  @Input() phxShowGroupCheckbox: boolean = true;
  /** Checked state of the group checkbox (consumer-managed). */
  @Input() phxGroupChecked: boolean = false;
  /** Indeterminate state of the group checkbox (consumer-managed). */
  @Input() phxGroupIndeterminate: boolean = false;

  /** Emitted when a clickable, non-disabled data row is clicked. */
  @Output() phxRowClick = new EventEmitter<T>();
  /** Emitted when a group header's expand/collapse state changes (two-way bindable). */
  @Output() phxGroupOpenChange = new EventEmitter<boolean>();
  /** Emitted when the group-header checkbox is toggled. */
  @Output() phxGroupCheckboxChange = new EventEmitter<boolean>();

  @ContentChildren(PhxCellDefDirective) public cellDirectives!: QueryList<PhxCellDefDirective>;
  @ContentChild(PhxGroupHeaderDirective) public groupHeader: PhxGroupHeaderDirective | undefined;

  public readonly ChevronIcon = ChevronDown;
  public readonly BulletIcon = Bullet16;
  public readonly trackByKey = trackByColumnKey;

  public selected = false;

  /** Visible columns config (filtered from service). */
  public visibleColumns: PhxTableColumnConfig[];

  /** Map of columnId → PhxCellDefDirective, built from [phxCellDef] directives. */
  public cellDirectiveMap = new Map<string, PhxCellDefDirective>();

  /** Map of columnId → pre-computed cell style info from table-level config. */
  public cellStyleMap = new Map<string, PhxCellStyleInfo>();

  private _destroy$ = new Subject<void>();

  constructor(
    private _tableService: PhxTableService,
    private _cdr: ChangeDetectorRef,
  ) {
    // Initialize eagerly so the view renders cells in the first CD cycle.
    this.visibleColumns = this._tableService.visibleColumns;
    this.cellStyleMap = this._tableService.cellStyleMap;
  }

  // ── Lifecycle ────────────────────────────────────────────

  public ngOnChanges(changes: SimpleChanges): void {
    const { phxDisabled, phxRowData } = changes;

    if (phxDisabled && phxRowData) {
      this._tableService.setRowDisabled(this.phxRowData.id, this.phxDisabled);
    }
  }

  public ngAfterContentInit(): void {
    if (this.phxRowData && this.phxDisabled) {
      this._tableService.setRowDisabled(this.phxRowData.id, true);
    }

    this.buildCellDirectiveMap();
    this.cellDirectives.changes.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.buildCellDirectiveMap();
      this._cdr.markForCheck();
    });

    this._tableService.rowStateChanged$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.visibleColumns = this._tableService.visibleColumns;
      this.cellStyleMap = this._tableService.cellStyleMap;
      this._cdr.markForCheck();
    });

    this._tableService.selectedIds$.pipe(takeUntil(this._destroy$)).subscribe(ids => {
      const next = this.phxRowData ? ids.has(this.phxRowData.id) : false;
      if (next !== this.selected) {
        this.selected = next;
        this._cdr.markForCheck();
      }
    });
  }

  public ngOnDestroy(): void {
    if (this.phxRowData && this.phxDisabled) {
      this._tableService.setRowDisabled(this.phxRowData.id, false);
    }
    this._destroy$.next();
    this._destroy$.complete();
  }

  /** Resolved clickable state: row-level override or inherited from table. */
  public get isClickable(): boolean {
    return this.phxClickable !== undefined ? this.phxClickable : this._tableService.clickable;
  }

  public get open(): boolean {
    return this.phxGroupOpen;
  }

  public get selectable(): boolean {
    return this._tableService.selectable;
  }

  public get checkboxStickyInfo(): PhxStickyColumnInfo | undefined {
    return this._tableService.stickyColumns.get(-1);
  }

  public onCheckboxChange(): void {
    if (this.phxDisabled || !this.phxRowData) return;
    this._tableService.toggleRow(this.phxRowData.id);
  }

  public onClick(): void {
    // Group-header rows toggle/select via their own controls — ignore the host click.
    if (this.phxGroupRow) return;
    if (this.phxDisabled || !this.isClickable) return;
    if (this.selectable && this.phxRowData) {
      this._tableService.toggleRow(this.phxRowData.id);
    }
    this.phxRowClick.emit(this.phxRowData);
  }

  // ── Group header ─────────────────────────────────────────

  /** Toggle expand/collapse from the chevron / header click. */
  public onGroupToggle(event?: Event): void {
    event?.stopPropagation();
    this.setGroupOpen(!this.phxGroupOpen);
  }

  /** Sets expand state; used by the chevron and the table accordion coordinator. */
  public setGroupOpen(open: boolean): void {
    if (open === this.phxGroupOpen) return;
    this.phxGroupOpen = open;
    this.phxGroupOpenChange.emit(open);
    this._cdr.markForCheck();
  }

  public onGroupCheckboxChange(checked: boolean): void {
    this.phxGroupCheckboxChange.emit(checked);
  }

  /** Build the columnId → PhxCellDefDirective map from [phxCellDef] directives. */
  private buildCellDirectiveMap(): void {
    this.cellDirectiveMap.clear();
    this.cellDirectives.forEach(d => {
      this.cellDirectiveMap.set(d.columnId, d);
    });
  }
}
